import * as z from "zod";
import { erpInvoiceStateDto } from "../erp/invoices";
import { type Caller, decimal, flag, pagination, type Result, version } from "../index";
import { syncErrorDto, syncFailures } from "../til";
import { bcDocumentTypes, creditNoteReasons, documentStatuses, documentTypes } from "./enums";
import type { IssueOrderConfirmationInput } from "./order-confirmation";

// Documents of a sale (M9, FR-M9-04…10, ADR-118; 50-api/documents.md): Nexxt asks, and numbers the invoices and the
// credit notes to a customer (C-48, ADR-255); Business Central registers them and numbers the rest; the Document
// Service makes the PDF.

/** Permission of the system Actor of `requestDocument`, given by the process that calls it: no role may hold it. */
export const DOCUMENT_REQUEST = "document.request";

/**
 * `requestDocument`, a command of the processes of sales (processi-e-viste.md §3, ADR-097): the invoice or the proforma
 * of a shipment. Its key is the caller's: one live document for each shipment and type (FR-M9-06, FR-M9-08), and the
 * same request again answers that document, whatever state it is in.
 */
export const requestDocumentInput = z.object({
  fulfillmentId: z.uuid(),
  type: z.enum(bcDocumentTypes),
});
export type RequestDocumentInput = z.input<typeof requestDocumentInput>;

/**
 * FR-M9-07: the Customer Service asks again for a document in error, or one Business Central has not registered in
 * time, once what made it fail is fixed. Same key towards Business Central, which never registers it twice (I-04 §4).
 * `version` is the one the client read (409 otherwise).
 */
export const retryDocumentInput = z.object({ documentId: z.uuid(), version });
export type RetryDocumentInput = z.input<typeof retryDocumentInput>;
/** A document by its id: `getDocument`, and the path of the routes of one document. */
export const documentParams = retryDocumentInput.pick({ documentId: true });
export type GetDocumentInput = z.input<typeof documentParams>;
export const retryDocumentBody = retryDocumentInput.omit({ documentId: true });

/**
 * ADR-178 §5.1: the Customer Service asks the Document Service again for the PDF of a numbered document whose PDF is
 * late (I-34, ADR-183). The same input as `retryDocument`: the answer is a new I-07, and `version` is the one the
 * client read (409 otherwise).
 */
export type RequestDocumentPdfInput = RetryDocumentInput;

/**
 * ADR-178 §5.2: the PDF of a document, by `documentParams`, for whoever reads the document (`document.read`): never
 * another file of the platform (ADR-183).
 */
export type DownloadDocumentPdfInput = GetDocumentInput;

/** Orders of the list; the id breaks ties. */
const documentSorts = ["-updatedAt", "-requestedAt"] as const;

const documentFilters = {
  status: z.enum(documentStatuses).optional(),
  type: z.enum(documentTypes).optional(),
  salesOrder: z.uuid().optional(),
  fulfillment: z.uuid().optional(),
  customer: z.uuid().optional(),
  /** Asked for and still without a number or a PDF past `documents.issue_overdue_minutes`: Business Central is late. */
  overdue: flag.optional(),
  /** What can be asked for again now (`canRetry`): in error, or late without its number (FR-M9-07, ADR-127). */
  retryable: flag.optional(),
  /** Part of the number Business Central gave the document, or of the number of its sales order, case-insensitive. */
  q: z.string().trim().min(1).max(40).optional(),
};

/** The documents the Actor sees: all of them for Tomorrow, its own for a customer or a supplier (RLS). */
export const listDocumentsInput = z.object({
  ...documentFilters,
  sort: z.enum(documentSorts).default("-updatedAt"),
  ...pagination({ defaultSize: 25, maxSize: 100 }),
});
export type ListDocumentsInput = z.input<typeof listDocumentsInput>;

/** How many documents the list has with the same filters, and the oldest of them (ADR-086): the queue of the home. */
export const countDocumentsInput = z.object(documentFilters);
export type CountDocumentsInput = z.input<typeof countDocumentsInput>;

/** A document. The amounts are the ones Business Central registered (I-05): Nexxt never computes them. */
export const documentDto = z
  .object({
    id: z.uuid(),
    type: z.enum(documentTypes),
    status: z.enum(documentStatuses),
    /** `reference.legal_entity.code` of the company that issues it (FR-M9-04). */
    company: z.string(),
    salesOrder: z.object({ id: z.uuid(), soNumber: z.string() }).nullable(),
    fulfillment: z.object({ id: z.uuid(), fulfillmentNumber: z.string() }).nullable(),
    customer: z.object({ id: z.uuid(), name: z.string() }).nullable(),
    /** A purchase document: the credit note of a supplier (ADR-230). */
    supplier: z.object({ id: z.uuid(), name: z.string() }).nullable().optional(),
    /** A credit note (ADR-230): what it credits, by its id in the domain where Finance approved it. */
    creditNoteOf: z
      .object({ reason: z.enum(creditNoteReasons), sourceId: z.uuid() })
      .nullable()
      .optional(),
    /**
     * Its fiscal number: the one Nexxt gave an invoice or a credit note to a customer when it asked for it (C-48,
     * ADR-255), the one Business Central gave the others once registered (FR-M9-05). `status` says whether BC
     * registered it.
     */
    documentNo: z.string().nullable(),
    documentDate: z.iso.date().nullable(),
    dueDate: z.iso.date().nullable(),
    currency: z.string().nullable(),
    taxAmount: decimal.nullable().optional(),
    totalAmount: decimal.nullable().optional(),
    /** The PDF of the Document Service (I-07), downloaded from `GET /documents/documents/{documentId}/pdf` (ADR-183). */
    pdfFileId: z.uuid().nullable(),
    /** The latest time the Customer Service asked the Document Service for the PDF again (I-34); null if never. */
    pdfRequestedAt: z.iso.datetime().nullable(),
    requestedAt: z.iso.datetime().nullable(),
    numberedAt: z.iso.datetime().nullable(),
    /** Asked for, and not numbered or without its PDF past `documents.issue_overdue_minutes`. */
    overdue: z.boolean(),
    /**
     * Whether it can be asked for again now (FR-M9-07): in error, or late without its number. The domain says it,
     * with the parameter of the lateness; `document.retry` is still needed (ADR-127).
     */
    canRetry: z.boolean(),
    /**
     * Whether its PDF can be asked for again now (ADR-178 §5.1): numbered, late without the PDF, and not asked for
     * again within `documents.issue_overdue_minutes`. `document.retry` is still needed (ADR-183).
     */
    canRequestPdf: z.boolean(),
    /** Why the document is in error (FR-M9-07): it could not be asked for, or its request did not end on BC. */
    errorCause: z.enum(syncFailures).nullable(),
    /** What was missing, or what Business Central refused, one per field. */
    errors: z.array(syncErrorDto),
    /** Times the document was asked for: 1 until it is relaunched. */
    attemptCount: z.number().int(),
    version,
    updatedAt: z.iso.datetime(),
  })
  .meta({ id: "Document" });
export type DocumentDto = z.output<typeof documentDto>;

/**
 * The commands of `DocumentsService` the processes of sales call (ADR-097): the entrypoint implements them, so the
 * caller and the binding cannot drift apart.
 */
export interface DocumentCommands {
  requestDocument(caller: Caller, input: RequestDocumentInput): Promise<Result<DocumentDto>>;
  /** The PDF of an order confirmation, made by Nexxt (ADR-198). */
  issueOrderConfirmation(caller: Caller, input: IssueOrderConfirmationInput): Promise<Result<DocumentDto>>;
}

/** Why an attempt did not end well: the cause of the document's error, or `overdue` when a relaunch replaced it late. */
export const attemptFailures = [...syncFailures, "overdue"] as const;
export type AttemptFailure = (typeof attemptFailures)[number];

/** A time a document was asked for, with how it ended (FR-M9-07). */
export const documentAttemptDto = z.object({
  attemptNo: z.number().int(),
  status: z.enum(documentStatuses),
  /** Why it did not end well; null while it waits, or once Business Central registered the document. */
  failure: z.enum(attemptFailures).nullable(),
  /** How many errors Business Central or the request had; the errors themselves are on the document. */
  errorCount: z.number().int(),
  startedAt: z.iso.datetime(),
  finishedAt: z.iso.datetime().nullable(),
  /** Name of who asked for it: the user the process ran for, or who relaunched it; null for a process of nobody's. */
  startedBy: z.string().nullable(),
});

/**
 * A document with every time it was asked for, the latest first; an invoice Nexxt numbered, as the invoices file to
 * the ERP took it (C-41, ADR-256): null for the other documents. Only Tomorrow reads it.
 */
export const documentDetailDto = documentDto
  .extend({ attempts: z.array(documentAttemptDto), erpInvoice: erpInvoiceStateDto.nullable().optional() })
  .meta({ id: "DocumentDetail" });
export type DocumentDetailDto = z.output<typeof documentDetailDto>;

// --- What a customer reads in Frontrow (P8.1f, ADR-220) ---

/**
 * `listCustomerDocuments`: the documents of the customer's orders that have their PDF (`pdf_available`, `sent`), the
 * latest issued first. Every user of the customer reads them (B-46); the RLS keeps them to its organization.
 */
export const listCustomerDocumentsInput = z.object({
  type: z.enum(documentTypes).optional(),
  salesOrder: z.uuid().optional(),
  fulfillment: z.uuid().optional(),
  /**
   * Part of the number Business Central gave the document, or of the number of its sales order or of its shipment (the
   * export pack has no number of its own, ADR-232), case-insensitive.
   */
  q: documentFilters.q,
  ...pagination({ defaultSize: 25, maxSize: 100 }),
});
export type ListCustomerDocumentsInput = z.input<typeof listCustomerDocumentsInput>;

/**
 * A document as its customer sees it: what it is, of which order and shipment, its number, dates and amounts as
 * Business Central registered them, and when its PDF came. Never why it failed, its attempts or the requests of
 * Tomorrow (ADR-220).
 */
export const customerDocumentDto = z
  .object({
    id: z.uuid(),
    type: z.enum(documentTypes),
    salesOrder: z.object({ id: z.uuid(), soNumber: z.string() }).nullable(),
    fulfillment: z.object({ id: z.uuid(), fulfillmentNumber: z.string() }).nullable(),
    /** The number Business Central gave it; null for an order confirmation, which Nexxt makes (ADR-198). */
    documentNo: z.string().nullable(),
    documentDate: z.iso.date().nullable(),
    dueDate: z.iso.date().nullable(),
    currency: z.string().nullable(),
    taxAmount: decimal.nullable().optional(),
    totalAmount: decimal.nullable().optional(),
    /** When its PDF came: from the Document Service (I-07), or made by Nexxt for a confirmation. */
    issuedAt: z.iso.datetime().nullable(),
  })
  .meta({ id: "CustomerDocument" });
export type CustomerDocumentDto = z.output<typeof customerDocumentDto>;
